import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Award, BookOpen, Globe, CheckCircle } from 'lucide-react';
import { centerInfo, stats, features, testimonials, courses } from '../utils/mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

export default function Home() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0039A6] via-[#0052d4] to-[#007A5E] text-white py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FCD116] rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Star className="text-[#FCD116]" size={18} fill="#FCD116" />
              <span className="text-sm font-medium">{centerInfo.rating} étoiles • {centerInfo.reviewCount} avis</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Apprenez le Russe, l'Anglais et le Chinois au Cameroun
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-white/90 font-light">
              {centerInfo.nameRu}
            </p>
            <p className="text-lg md:text-xl mb-8 text-white/80 max-w-2xl mx-auto">
              Centre d'excellence pour l'apprentissage de la langue russe à Douala. 
              Préparez votre avenir académique et professionnel avec nos cours certifiés.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-[#D52B1E] hover:bg-[#b82418] text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
                  Inscription Gratuite
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/courses">
                <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0039A6] px-8 py-6 text-lg transition-all transform hover:-translate-y-1">
                  Découvrir nos Cours
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#0039A6] mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi Nous Choisir ?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Une expérience d'apprentissage unique qui combine excellence pédagogique et immersion culturelle
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-t-4 border-t-[#D52B1E] hover:shadow-xl transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl text-[#0039A6]">{feature.title}</CardTitle>
                  <p className="text-sm text-gray-500">{feature.titleRu}</p>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Preview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos Cours Populaires
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Des programmes adaptés à tous les niveaux, du débutant à l'expert
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.slice(0, 3).map((course) => (
              <Card key={course.id} className="hover:shadow-xl transition-all transform hover:-translate-y-2">
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <span className="bg-[#007A5E] text-white text-xs px-3 py-1 rounded-full">
                      {course.level}
                    </span>
                    <span className="text-[#0039A6] font-bold">{course.price}</span>
                  </div>
                  <CardTitle className="text-xl">{course.title}</CardTitle>
                  <p className="text-sm text-gray-500">{course.titleRu}</p>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{course.description}</p>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} className="text-[#FCD116]" />
                      <span>Durée: {course.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-[#FCD116]" />
                      <span>{course.students} étudiants inscrits</span>
                    </div>
                  </div>
                  <Link to="/courses">
                    <Button className="w-full mt-4 bg-[#0039A6] hover:bg-[#0052d4]">
                      En savoir plus
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/courses">
              <Button size="lg" variant="outline" className="border-2 border-[#0039A6] text-[#0039A6] hover:bg-[#0039A6] hover:text-white">
                Voir tous les cours
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-[#007A5E] to-[#00654d] text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ce que Disent Nos Étudiants
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Des témoignages authentiques de nos anciens étudiants
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} fill="#FCD116" className="text-[#FCD116]" />
                    ))}
                  </div>
                  <p className="text-white/90 mb-4 italic">"{testimonial.text}"</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-white">{testimonial.name}</p>
                      <p className="text-sm text-white/70">{testimonial.course}</p>
                    </div>
                    <span className="text-sm text-white/60">{testimonial.year}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0039A6] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Prêt à Commencer Votre Voyage Linguistique ?
          </h2>
          <p className="text-xl mb-8 text-white/80 max-w-2xl mx-auto">
            Rejoignez plus de 500 étudiants qui ont transformé leur avenir avec nous
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#D52B1E] hover:bg-[#b82418] text-white px-10 py-6 text-lg shadow-2xl hover:shadow-3xl transition-all transform hover:-translate-y-1">
              Commencer Maintenant
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
